import { useState } from 'react';

interface Friend {
  id: string;
  name: string;
  avatar: string;
  level: number;
  xp: number;
  badges: number;
  isOnline: boolean;
  lastActive: string;
  mutualFriends: number;
  status: 'confirmed' | 'pending' | 'suggested';
}

interface FriendsListProps {
  confirmedFriends?: Friend[];
  onViewProfile?: (friendId: string) => void;
  onRemoveFriend?: (friendId: string) => void;
}

export function FriendsList({ confirmedFriends = [], onViewProfile, onRemoveFriend }: FriendsListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'level' | 'xp'>('name');
  const [activeSubTab, setActiveSubTab] = useState<'friends' | 'search' | 'requests'>('friends');
  const [searchResults, setSearchResults] = useState<Friend[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [pendingRequests, setPendingRequests] = useState<Friend[]>([
    {
      id: 'p1',
      name: 'Julia Ferreira',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20business%20woman%20portrait%20headshot%20confident%20smile%20corporate%20attire%20clean%20background&width=100&height=100&seq=pending1&orientation=squarish',
      level: 8,
      xp: 5200,
      badges: 14,
      isOnline: true,
      lastActive: 'Online agora',
      mutualFriends: 3,
      status: 'pending'
    },
    {
      id: 'p2',
      name: 'Lucas Almeida',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20business%20man%20portrait%20headshot%20confident%20smile%20corporate%20attire%20clean%20background&width=100&height=100&seq=pending2&orientation=squarish',
      level: 11,
      xp: 8100,
      badges: 22,
      isOnline: false,
      lastActive: 'Há 30 min',
      mutualFriends: 5,
      status: 'pending'
    }
  ]);

  // Mock de pessoas sugeridas para busca
  const suggestedPeople: Friend[] = [
    {
      id: 's1',
      name: 'Fernanda Lima',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20business%20woman%20portrait%20headshot%20confident%20smile%20corporate%20attire%20clean%20background&width=100&height=100&seq=suggested1&orientation=squarish',
      level: 13,
      xp: 9500,
      badges: 26,
      isOnline: true,
      lastActive: 'Online agora',
      mutualFriends: 7,
      status: 'suggested'
    },
    {
      id: 's2',
      name: 'Bruno Cardoso',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20business%20man%20portrait%20headshot%20confident%20smile%20corporate%20attire%20clean%20background&width=100&height=100&seq=suggested2&orientation=squarish',
      level: 10,
      xp: 7500,
      badges: 19,
      isOnline: false,
      lastActive: 'Há 1 hora',
      mutualFriends: 4,
      status: 'suggested'
    },
    {
      id: 's3',
      name: 'Camila Rocha',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20business%20woman%20portrait%20headshot%20confident%20smile%20corporate%20attire%20clean%20background&width=100&height=100&seq=suggested3&orientation=squarish',
      level: 12,
      xp: 8800,
      badges: 23,
      isOnline: true,
      lastActive: 'Online agora',
      mutualFriends: 9,
      status: 'suggested'
    },
    {
      id: 's4',
      name: 'Diego Santos',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20business%20man%20portrait%20headshot%20confident%20smile%20corporate%20attire%20clean%20background&width=100&height=100&seq=suggested4&orientation=squarish',
      level: 9,
      xp: 6500,
      badges: 17,
      isOnline: false,
      lastActive: 'Há 3 horas',
      mutualFriends: 2,
      status: 'suggested'
    },
    {
      id: 's5',
      name: 'Patrícia Souza',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20business%20woman%20portrait%20headshot%20confident%20smile%20corporate%20attire%20clean%20background&width=100&height=100&seq=suggested5&orientation=squarish',
      level: 14,
      xp: 10200,
      badges: 29,
      isOnline: true,
      lastActive: 'Online agora',
      mutualFriends: 11,
      status: 'suggested'
    }
  ];

  const handleSearch = () => {
    if (searchQuery.trim()) {
      const results = suggestedPeople.filter(person =>
        person.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setSearchResults(results);
    } else {
      setSearchResults(suggestedPeople);
    }
  };

  const handleSendRequest = (personId: string) => {
    const person = searchResults.find(p => p.id === personId);
    if (person) {
      alert(`Solicitação de amizade enviada para ${person.name}!`);
      setSearchResults(searchResults.filter(p => p.id !== personId));
    }
  };

  const handleAcceptRequest = (requestId: string) => {
    const request = pendingRequests.find(r => r.id === requestId);
    if (request) {
      alert(`Você aceitou a solicitação de ${request.name}!`);
      setPendingRequests(pendingRequests.filter(r => r.id !== requestId));
    }
  };

  const handleRejectRequest = (requestId: string) => {
    const request = pendingRequests.find(r => r.id === requestId);
    if (request) {
      alert(`Você recusou a solicitação de ${request.name}.`);
      setPendingRequests(pendingRequests.filter(r => r.id !== requestId));
    }
  };

  const filteredFriends = confirmedFriends
    .filter((friend) =>
      friend.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'level') return b.level - a.level;
      return b.xp - a.xp;
    });

  return (
    <div className="space-y-6">
      {/* Sub-abas */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2">
        <div className="flex space-x-1">
          <button
            onClick={() => setActiveSubTab('friends')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all whitespace-nowrap text-sm ${
              activeSubTab === 'friends'
                ? 'bg-teal-500 text-white shadow-md'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <i className="ri-user-heart-line"></i>
            <span>Meus Amigos</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
              activeSubTab === 'friends' ? 'bg-white/20' : 'bg-gray-200'
            }`}>
              {confirmedFriends.length}
            </span>
          </button>
          <button
            onClick={() => setActiveSubTab('search')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all whitespace-nowrap text-sm ${
              activeSubTab === 'search'
                ? 'bg-teal-500 text-white shadow-md'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <i className="ri-user-search-line"></i>
            <span>Buscar Pessoas</span>
          </button>
          <button
            onClick={() => setActiveSubTab('requests')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all whitespace-nowrap text-sm ${
              activeSubTab === 'requests'
                ? 'bg-teal-500 text-white shadow-md'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <i className="ri-user-add-line"></i>
            <span>Solicitações</span>
            {pendingRequests.length > 0 && (
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                activeSubTab === 'requests' ? 'bg-white/20' : 'bg-red-500 text-white'
              }`}>
                {pendingRequests.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Conteúdo - Meus Amigos */}
      {activeSubTab === 'friends' && (
        <div className="space-y-6">
          {/* Busca e Filtros */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Buscar amigos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              />
            </div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'name' | 'level' | 'xp')}
              className="px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
            >
              <option value="name">Ordenar por Nome</option>
              <option value="level">Ordenar por Nível</option>
              <option value="xp">Ordenar por XP</option>
            </select>
          </div>

          {/* Lista de Amigos */}
          {filteredFriends.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredFriends.map((friend) => (
                <div
                  key={friend.id}
                  className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-lg transition-all cursor-pointer group"
                >
                  <div className="flex items-start gap-3">
                    <div className="relative">
                      <img
                        src={friend.avatar}
                        alt={friend.name}
                        className="w-14 h-14 rounded-full object-cover"
                      />
                      {friend.isOnline && (
                        <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 border-2 border-white rounded-full"></div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate group-hover:text-teal-600 transition-colors">
                        {friend.name}
                      </h3>
                      <p className="text-xs text-gray-500">{friend.lastActive}</p>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-3 gap-2">
                    <div className="text-center">
                      <div className="text-lg font-bold text-teal-600">{friend.level}</div>
                      <div className="text-xs text-gray-500">Nível</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-purple-600">{friend.xp.toLocaleString()}</div>
                      <div className="text-xs text-gray-500">XP</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-orange-600">{friend.badges}</div>
                      <div className="text-xs text-gray-500">Badges</div>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                      <i className="ri-user-line mr-1"></i>
                      {friend.mutualFriends} em comum
                    </span>
                    <div className="flex gap-2">
                      {onViewProfile && (
                        <button 
                          onClick={() => onViewProfile(friend.id)}
                          className="text-teal-600 hover:text-teal-700 transition-colors"
                        >
                          <i className="ri-eye-line text-lg"></i>
                        </button>
                      )}
                      <button className="text-teal-600 hover:text-teal-700 transition-colors">
                        <i className="ri-message-3-line text-lg"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-md border border-gray-200 p-12 text-center">
              <i className="ri-user-heart-line text-6xl text-gray-300 mb-4"></i>
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                {searchTerm ? 'Nenhum amigo encontrado' : 'Você ainda não tem amigos'}
              </h3>
              <p className="text-gray-600 mb-6">
                {searchTerm ? 'Tente buscar com outro termo' : 'Comece adicionando pessoas na aba "Buscar Pessoas"!'}
              </p>
              {!searchTerm && (
                <button
                  onClick={() => setActiveSubTab('search')}
                  className="px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg font-medium hover:shadow-lg transition-all whitespace-nowrap"
                >
                  <i className="ri-user-search-line mr-2"></i>
                  Buscar Pessoas
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Conteúdo - Buscar Pessoas */}
      {activeSubTab === 'search' && (
        <div className="space-y-6">
          {/* Barra de Busca */}
          <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl p-6 border border-teal-100">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-user-search-line text-teal-600"></i>
              Encontre Novos Amigos
            </h3>
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input
                  type="text"
                  placeholder="Digite o nome da pessoa..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                />
              </div>
              <button
                onClick={handleSearch}
                className="px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg font-medium hover:shadow-lg transition-all whitespace-nowrap"
              >
                <i className="ri-search-line mr-2"></i>
                Buscar
              </button>
            </div>
          </div>

          {/* Resultados da Busca */}
          {searchResults.length > 0 ? (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-4">
                {searchQuery ? `Resultados para "${searchQuery}"` : 'Pessoas Sugeridas'}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {searchResults.map((person) => (
                  <div
                    key={person.id}
                    className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-lg transition-all"
                  >
                    <div className="flex items-start gap-3 mb-4">
                      <div className="relative">
                        <img
                          src={person.avatar}
                          alt={person.name}
                          className="w-14 h-14 rounded-full object-cover"
                        />
                        {person.isOnline && (
                          <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 border-2 border-white rounded-full"></div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate">
                          {person.name}
                        </h3>
                        <p className="text-xs text-gray-500">Nível {person.level}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-2 mb-4">
                      <div className="text-center">
                        <div className="text-sm font-bold text-teal-600">{person.level}</div>
                        <div className="text-xs text-gray-500">Nível</div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm font-bold text-purple-600">{person.xp.toLocaleString()}</div>
                        <div className="text-xs text-gray-500">XP</div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm font-bold text-orange-600">{person.badges}</div>
                        <div className="text-xs text-gray-500">Badges</div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <span className="text-xs text-gray-500">
                        <i className="ri-user-line mr-1"></i>
                        {person.mutualFriends} em comum
                      </span>
                      <button
                        onClick={() => handleSendRequest(person.id)}
                        className="px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg text-sm font-medium hover:shadow-lg transition-all whitespace-nowrap"
                      >
                        <i className="ri-user-add-line mr-1"></i>
                        Adicionar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : searchQuery ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <i className="ri-user-search-line text-6xl text-gray-300 mb-4"></i>
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                Nenhuma pessoa encontrada
              </h3>
              <p className="text-gray-600">
                Tente buscar com outro nome
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <i className="ri-search-line text-6xl text-gray-300 mb-4"></i>
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                Busque por pessoas
              </h3>
              <p className="text-gray-600">
                Digite um nome acima para encontrar novos amigos
              </p>
            </div>
          )}
        </div>
      )}

      {/* Conteúdo - Solicitações */}
      {activeSubTab === 'requests' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl p-6 border border-orange-100">
            <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
              <i className="ri-user-add-line text-orange-600"></i>
              Solicitações de Amizade
            </h3>
            <p className="text-sm text-gray-600">
              Você tem {pendingRequests.length} solicitação{pendingRequests.length !== 1 ? 'ões' : ''} pendente{pendingRequests.length !== 1 ? 's' : ''}
            </p>
          </div>

          {pendingRequests.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {pendingRequests.map((request) => (
                <div
                  key={request.id}
                  className="bg-white border-2 border-orange-200 rounded-xl p-4 hover:shadow-lg transition-all"
                >
                  <div className="flex items-start gap-3 mb-4">
                    <div className="relative">
                      <img
                        src={request.avatar}
                        alt={request.name}
                        className="w-14 h-14 rounded-full object-cover"
                      />
                      {request.isOnline && (
                        <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 border-2 border-white rounded-full"></div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">
                        {request.name}
                      </h3>
                      <p className="text-xs text-gray-500">Nível {request.level}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <div className="text-center">
                      <div className="text-sm font-bold text-teal-600">{request.level}</div>
                      <div className="text-xs text-gray-500">Nível</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-bold text-purple-600">{request.xp.toLocaleString()}</div>
                      <div className="text-xs text-gray-500">XP</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-bold text-orange-600">{request.badges}</div>
                      <div className="text-xs text-gray-500">Badges</div>
                    </div>
                  </div>

                  <div className="space-y-2 pt-4 border-t border-gray-100">
                    <span className="text-xs text-gray-500 block mb-3">
                      <i className="ri-user-line mr-1"></i>
                      {request.mutualFriends} amigo{request.mutualFriends !== 1 ? 's' : ''} em comum
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleAcceptRequest(request.id)}
                        className="flex-1 px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg text-sm font-medium hover:shadow-lg transition-all whitespace-nowrap"
                      >
                        <i className="ri-check-line mr-1"></i>
                        Aceitar
                      </button>
                      <button
                        onClick={() => handleRejectRequest(request.id)}
                        className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 transition-all whitespace-nowrap"
                      >
                        <i className="ri-close-line mr-1"></i>
                        Recusar
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <i className="ri-user-add-line text-6xl text-gray-300 mb-4"></i>
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                Nenhuma solicitação pendente
              </h3>
              <p className="text-gray-600 mb-6">
                Você não tem solicitações de amizade no momento
              </p>
              <button
                onClick={() => setActiveSubTab('search')}
                className="px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg font-medium hover:shadow-lg transition-all whitespace-nowrap"
              >
                <i className="ri-user-search-line mr-2"></i>
                Buscar Pessoas
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
